import { useConvex, useQuery } from "convex/react";
import {
  ChevronDown,
  Cloud,
  Copy,
  FolderOpen,
  HardDrive,
  Layers,
  Lock,
  Pencil,
  Plus,
  Server,
  Settings,
  Trash2,
  User,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { PanoramaIcon } from "@/icons/PanoramaIcon";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { S3ConnectionDialog } from "@/components/s3/S3ConnectionDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CraftspaceSelector } from "@/components/CraftspaceSelector";
import { CraftspaceSidebar, type DashboardView } from "@/components/CraftspaceSidebar";
import { PublishedToursList } from "@/components/editor/PublishedToursList";
import CraftspaceVariables from "@/pages/CraftspaceVariables";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { useIndexedDBCache } from "@/hooks/useIndexedDBCache";
import { StorageServiceFactory } from "@/services/storage/StorageServiceFactory";
import { EncryptedS3CredentialService } from "@/services/s3/EncryptedS3CredentialService";
import { StorageService } from "@/services/storageService";
import type { Tables } from "@/types/database";
import { createProjectFolders, createCraftspaceFolders } from "@/utils/createStorageFolders";
import { resolveStoragePathSegments } from "@/utils/storagePaths";
import { getCraftspaceFavicon, isUrl } from "@/utils/faviconUtils";
import { manualRefreshCraftspaceUrls } from "@/utils/manualUrlRefresh";
import { deleteProjectCompletely } from "@/utils/projectStorageCleanup";
import { moveProjectToCraftspace as moveLocalProjectToCraftspace } from "@/data/adapters/local/projects";
import { reprocessPanoramas } from "@/utils/reprocessPanoramas";
import { getUpgradeMessage, getUserSubscriptionInfo } from "@/utils/craftspacePermissions";
import { getMostRecentCraftspace, setRecentCraftspace } from "@/utils/craftspaceTracking";
import { api } from "../../convex/_generated/api";
import { isElectron } from "@/services/storage/LocalFileSystemClient";
import {
  createCraftspace as createLocalCraftspace,
  createProject as createLocalProject,
  updateProject as updateLocalProject,
  useCraftspaces as useLocalCraftspaces,
  useProjects as useLocalProjects,
} from "@/data";
import { CMSService } from "@/services/cmsService";

type Project = Tables<"projects">;
type Panorama = Tables<"panoramas">;

// Cached project thumbnail component that loads from IndexedDB
interface CachedProjectThumbnailProps {
  panoramaId: string;
  projectName: string;
  projectId: string;
  craftspaceId: string;
  getThumbnail: (
    panoramaId: string,
    isWebP?: boolean,
  ) => Promise<{ data: ArrayBuffer; mimeType: string } | null>;
  cacheThumbnailFromUrl: (
    panoramaId: string,
    projectId: string,
    craftspaceId: string,
    url: string,
    isWebP?: boolean,
  ) => Promise<void>;
  networkThumbnailUrl?: string; // Optional: if the thumbnail is currently loading from a network URL
}

const CachedProjectThumbnail = ({
  panoramaId,
  projectName,
  projectId,
  craftspaceId,
  getThumbnail,
  cacheThumbnailFromUrl,
  networkThumbnailUrl,
}: CachedProjectThumbnailProps) => {
  const [cachedThumbnailUrl, setCachedThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isReCaching, setIsReCaching] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regenerationAttempted, setRegenerationAttempted] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [networkBlobUrl, setNetworkBlobUrl] = useState<string | null>(null);
  const [networkLoadError, setNetworkLoadError] = useState(false);
  const [networkRetryCount, setNetworkRetryCount] = useState(0);
  const maxNetworkRetries = 2;
  const { toast } = useToast();
  const imgRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasLoaded) {
            setIsInView(true);
            setHasLoaded(true);
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, [hasLoaded]);

  // Load from cache only when in view
  useEffect(() => {
    if (!isInView || !panoramaId) return;

    const loadFromCache = async () => {
      try {
        setIsLoading(true);

        // Try WebP first, then JPEG
        let cachedThumbnail = await getThumbnail(panoramaId, true); // WebP
        if (!cachedThumbnail) {
          cachedThumbnail = await getThumbnail(panoramaId, false); // JPEG
        }

        if (cachedThumbnail) {
          const blob = new Blob([cachedThumbnail.data], { type: cachedThumbnail.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          setCachedThumbnailUrl(blobUrl);
        } else if (networkThumbnailUrl) {
          // No cached thumbnail available, will fall back to network URL in render
          console.log(
            `🌐 No cached thumbnail for ${projectName}, will use network URL:`,
            networkThumbnailUrl,
          );
        }
      } catch (error) {
        console.warn("Failed to load project thumbnail from cache:", error);
        // On error, ensure we fall back to network URL if available
        if (networkThumbnailUrl) {
          console.log(
            `🌐 Cache error for ${projectName}, falling back to network URL:`,
            networkThumbnailUrl,
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadFromCache();
  }, [isInView, panoramaId, getThumbnail, projectName, networkThumbnailUrl]);

  // Function to re-cache thumbnail from network URL
  const reCacheThumbnail = useCallback(
    async (url: string, isWebP: boolean = false) => {
      if (!panoramaId || isReCaching || !craftspaceId) {
        return;
      }

      try {
        setIsReCaching(true);

        await cacheThumbnailFromUrl(panoramaId, projectId, craftspaceId, url, isWebP);

        // After successful caching, try to load from cache again
        const cachedThumbnail = await getThumbnail(panoramaId, isWebP);
        if (cachedThumbnail) {
          const blob = new Blob([cachedThumbnail.data], { type: cachedThumbnail.mimeType });
          const blobUrl = URL.createObjectURL(blob);
          setCachedThumbnailUrl(blobUrl);
        } else {
          console.warn(`Still no cached thumbnail found for ${projectName} after re-caching`);
        }
      } catch (error) {
        console.error(`❌ Failed to re-cache project thumbnail for ${projectName}:`, error);
      } finally {
        setIsReCaching(false);
      }
    },
    [
      panoramaId,
      projectName,
      projectId,
      craftspaceId,
      isReCaching,
      cacheThumbnailFromUrl,
      getThumbnail,
    ],
  );

  // Function to regenerate thumbnail
  const regenerateThumbnail = useCallback(async () => {
    if (!panoramaId || isRegenerating || !craftspaceId) {
      console.log("⚠️ Skipping thumbnail regeneration:", {
        panoramaId,
        isRegenerating,
        craftspaceId,
      });
      return;
    }

    try {
      setIsRegenerating(true);
      setRegenerationAttempted(true);
      // console.log(`🔄 Attempting to regenerate thumbnail for ${projectName} (panorama: ${panoramaId})`);

      // Import the reprocessPanoramas function dynamically
      const { reprocessPanoramas } = await import("@/utils/reprocessPanoramas");

      // Trigger reprocessing for this specific panorama
      const result = await reprocessPanoramas(panoramaId);

      if (result.success) {
        // console.log(`✅ Thumbnail regeneration initiated for ${projectName}`);

        // Wait a bit for processing to start, then refresh
        setTimeout(async () => {
          try {
            // Try to load the newly generated thumbnail
            let cachedThumbnail = await getThumbnail(panoramaId, true); // WebP
            if (!cachedThumbnail) {
              cachedThumbnail = await getThumbnail(panoramaId, false); // JPEG
            }

              if (cachedThumbnail) {
                const blob = new Blob([cachedThumbnail.data], { type: cachedThumbnail.mimeType });
                const blobUrl = URL.createObjectURL(blob);
                setCachedThumbnailUrl(blobUrl);
                // console.log(`✅ Newly generated thumbnail loaded for ${projectName}`);
              } else {
                console.log(`⏳ Thumbnail still processing for ${projectName}, will retry later`);
              }
          } catch (error) {
            console.warn(`⚠️ Failed to load newly generated thumbnail for ${projectName}:`, error);
          }
        }, 3000); // Wait 3 seconds for processing to start
      } else {
        console.error(`❌ Failed to regenerate thumbnail for ${projectName}:`, result.error);

        toast({
          variant: "destructive",
          title: "Regeneration Failed",
          description: `Failed to regenerate thumbnail for ${projectName}. Please try again.`,
        });
      }
    } catch (error) {
      console.error(`❌ Error regenerating thumbnail for ${projectName}:`, error);

      toast({
        variant: "destructive",
        title: "Error",
        description: `An error occurred while regenerating the thumbnail for ${projectName}.`,
      });
    } finally {
      setIsRegenerating(false);
    }
  }, [panoramaId, projectName, craftspaceId, isRegenerating, getThumbnail, toast]);

  // Auto-attempt regeneration once when no thumbnail is available
  useEffect(() => {
    if (
      !networkThumbnailUrl &&
      !cachedThumbnailUrl &&
      !isLoading &&
      !regenerationAttempted &&
      panoramaId &&
      !isElectron() // Electron: local thumbnails don't need cloud regeneration
    ) {
      // console.log(`🔄 Auto-attempting thumbnail regeneration for ${projectName}`);

      toast({
        title: "Auto-Regenerating Thumbnail",
        description: `No thumbnail found for ${projectName}. Attempting to regenerate automatically...`,
      });

      regenerateThumbnail();
    }
  }, [
    networkThumbnailUrl,
    cachedThumbnailUrl,
    isLoading,
    regenerationAttempted,
    panoramaId,
    projectName,
    regenerateThumbnail,
    toast,
  ]);

  // Cleanup blob URL when component unmounts
  useEffect(() => {
    return () => {
      if (cachedThumbnailUrl) {
        URL.revokeObjectURL(cachedThumbnailUrl);
      }
    };
  }, [cachedThumbnailUrl]);

  // Reset blob URL and error when network URL changes
  useEffect(() => {
    const currentUrl = networkThumbnailUrl;
    return () => {
      void currentUrl;
      // Cleanup previous blob URL when URL changes
      if (networkBlobUrl) {
        URL.revokeObjectURL(networkBlobUrl);
      }
    };
  }, [networkBlobUrl, networkThumbnailUrl]);

  // Reset state when network URL changes
  useEffect(() => {
    if (networkThumbnailUrl !== undefined) {
      setNetworkBlobUrl(null);
      setNetworkLoadError(false);
      setNetworkRetryCount(0);
    }
  }, [networkThumbnailUrl]);

  // Fetch image via fetch API to bypass CORS issues in Cursor browser
  useEffect(() => {
    // app:// URLs (Electron local files) are served directly via <img> — fetch() doesn't support app://
    if (!networkThumbnailUrl || isLoading || networkBlobUrl || networkLoadError || networkThumbnailUrl.startsWith("app://")) return;

    let cancelled = false;

    const loadImageViaFetch = async () => {
      try {
        const response = await fetch(networkThumbnailUrl, {
          method: "GET",
          mode: "cors",
          credentials: "omit",
          referrerPolicy: "no-referrer",
        });

        if (cancelled) return;

        if (!response.ok) {
          if (response.status === 404) {
            setNetworkLoadError(true);
            regenerateThumbnail();
            return;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        if (cancelled) return;

        const blobUrl = URL.createObjectURL(blob);
        setNetworkBlobUrl(blobUrl);

        // Trigger caching when network thumbnail loads successfully
        const isWebP = networkThumbnailUrl.includes(".webp");
        reCacheThumbnail(networkThumbnailUrl, isWebP);
      } catch (error) {
        if (cancelled) return;

        console.error(`❌ Failed to fetch thumbnail for ${projectName}:`, error);
        if (networkRetryCount < maxNetworkRetries) {
          setTimeout(() => {
            if (!cancelled) {
              setNetworkRetryCount((prev) => prev + 1);
            }
          }, 500);
          return;
        }

        setNetworkLoadError(true);

        // Try to refresh the URL if it's a 403/expired error
        if (
          networkThumbnailUrl.includes("crafter.learn.360creators.com") &&
          networkThumbnailUrl.includes("thumbnail_")
        ) {
          return;
        }

        try {
          const headResponse = await fetch(networkThumbnailUrl, {
            method: "HEAD",
            referrerPolicy: "no-referrer",
          });
          if (headResponse.status === 403) {
            const { refreshSingleUrl } = await import("@/utils/refreshPrivateUrls");
            const refreshedUrl = await refreshSingleUrl(networkThumbnailUrl, craftspaceId);

            if (refreshedUrl && refreshedUrl !== networkThumbnailUrl) {
              await cacheThumbnailFromUrl(panoramaId, projectId, craftspaceId, refreshedUrl);
              return;
            }
          } else if (headResponse.status === 400) {
            regenerateThumbnail();
            return;
          }
        } catch (refreshError) {
          console.warn(`⚠️ Failed to refresh thumbnail URL for ${projectName}:`, refreshError);
          regenerateThumbnail();
        }
      }
    };

    loadImageViaFetch();

    return () => {
      cancelled = true;
    };
  }, [
    cacheThumbnailFromUrl,
    isLoading,
    networkBlobUrl,
    networkLoadError,
    networkRetryCount,
    networkThumbnailUrl,
    panoramaId,
    projectId,
    projectName,
    reCacheThumbnail,
    regenerateThumbnail,
    craftspaceId,
  ]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (networkBlobUrl) {
        URL.revokeObjectURL(networkBlobUrl);
      }
    };
  }, [networkBlobUrl]);

  // Show placeholder if not in view
  if (!isInView) {
    return (
      <div ref={imgRef} className="flex items-center justify-center h-full bg-gray-800">
        <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
      </div>
    );
  }

  // Electron local files: app:// URLs load natively via <img> — no fetch/cache pipeline needed
  if (networkThumbnailUrl?.startsWith("app://")) {
    return (
        <img
          src={networkThumbnailUrl}
          alt={`${projectName} thumbnail`}
          className="w-full h-full object-cover"
          crossOrigin="anonymous"
          referrerPolicy="no-referrer"
        />
    );
  }

  // Show loading state while checking cache or re-caching
  if (isLoading || isReCaching) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-800">
        <div className="w-4 h-4 bg-blue-400 rounded-full animate-pulse"></div>
      </div>
    );
  }

  // Show cached thumbnail if available (preferred)
  if (cachedThumbnailUrl) {
    return (
      <img
        src={cachedThumbnailUrl}
        alt={`${projectName} thumbnail (cached)`}
        className="w-full h-full object-cover"
      />
    );
  }

  if (networkThumbnailUrl && !isLoading) {
    if (networkBlobUrl) {
      return (
        <img
          src={networkBlobUrl}
          alt={`${projectName} thumbnail (network)`}
          className="w-full h-full object-cover"
          onLoad={() => {
            console.log(`🖼️ Network thumbnail loaded for ${projectName}`);
          }}
        />
      );
    }

    // Try direct img tag approach as fallback - Cursor browser might allow this even if fetch fails
    if (!networkLoadError) {
      return (
        <div className="relative">
          <img
            src={networkThumbnailUrl}
            alt={`${projectName} thumbnail (network)`}
            className="w-full h-full object-cover"
            loading="lazy"
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
            onLoad={() => {
              console.log(`🖼️ Direct network thumbnail loaded for ${projectName}`);
              // Trigger caching when network thumbnail loads successfully
              const isWebP = networkThumbnailUrl.includes(".webp");
              reCacheThumbnail(networkThumbnailUrl, isWebP);
            }}
            onError={async (_e) => {
              console.error(`❌ Direct network thumbnail failed for ${projectName}`);
              if (networkRetryCount < maxNetworkRetries) {
                setTimeout(() => {
                  setNetworkRetryCount((prev) => prev + 1);
                }, 500);
                return;
              }

              // Only set error after retry attempts are exhausted
              setNetworkLoadError(true);

              // Try fetch approach as fallback
              try {
                const response = await fetch(networkThumbnailUrl, {
                  method: "GET",
                  mode: "cors",
                  credentials: "omit",
                  referrerPolicy: "no-referrer",
                });

                if (!response.ok) {
                  throw new Error(`HTTP ${response.status}: ${response.statusText}`);
                }

                const blob = await response.blob();
                const blobUrl = URL.createObjectURL(blob);
                setNetworkBlobUrl(blobUrl);

                const isWebP = networkThumbnailUrl.includes(".webp");
                reCacheThumbnail(networkThumbnailUrl, isWebP);
              } catch (fetchError) {
                console.error(`❌ Fetch also failed for ${projectName}:`, fetchError);
                if (networkRetryCount < maxNetworkRetries) {
                  setTimeout(() => {
                    setNetworkRetryCount((prev) => prev + 1);
                  }, 500);
                  return;
                }
                setNetworkLoadError(true);
              }
            }}
          />
        </div>
      );
    }

    if (networkLoadError) {
      return (
        <div className="flex flex-col items-center justify-center h-full bg-gray-800 text-gray-400 p-4">
          <Lock size={32} className="mb-2" />
          <div className="text-xs text-center mb-3">Failed to load thumbnail</div>
        </div>
      );
    }
  }

  // Show regeneration interface if no thumbnail is available
  return (
    <div className="flex flex-col items-center justify-center h-full bg-gray-800 text-gray-400 p-4">
      <Lock size={32} className="mb-2" />
      <div className="text-xs text-center mb-3">No thumbnail available</div>
      <button
        type="button"
        onClick={regenerateThumbnail}
        disabled={isRegenerating}
        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white text-xs rounded transition-colors"
      >
        {isRegenerating ? "Regenerating..." : "Regenerate"}
      </button>
    </div>
  );
};

interface ProjectWithPanorama extends Project {
  firstPanorama?: Panorama;
}

interface CraftspaceGroup {
  craftspaceId: string;
  craftspaceName: string;
  projects: ProjectWithPanorama[];
  storageProvider?: "360crafter" | "custom-s3";
  _source?: "local" | "cloud";
}

interface Craftspace {
  id: string;
  name: string;
  created_at: string;
  storage_provider?: "360crafter" | "custom-s3";
  icon_url?: string | null;
  order?: number | null;
  is_owner?: boolean;
  access_role?: "owner" | "editor";
}

const timeAgo = (dateStr: string): string => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return mins <= 1 ? "Edited just now" : `Edited ${mins} minutes ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return hrs === 1 ? "Edited 1 hour ago" : `Edited ${hrs} hours ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return days === 1 ? "Edited 1 day ago" : `Edited ${days} days ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return months === 1 ? "Edited 1 month ago" : `Edited ${months} months ago`;
  const years = Math.floor(months / 12);
  return years === 1 ? "Edited 1 year ago" : `Edited ${years} years ago`;
};

const dashboardViews: DashboardView[] = [
  "projects",
  "published",
  "variables-colors",
  "variables-icons",
  "variables-sizes",
  "variables-font",
];

const viewToParam: Record<DashboardView, string> = {
  projects: "projects",
  published: "published",
  "variables-font": "font",
  "variables-colors": "colors",
  "variables-sizes": "sizes",
  "variables-icons": "icons",
};

const paramToView: Record<string, DashboardView> = {
  projects: "projects",
  published: "published",
  font: "variables-font",
  colors: "variables-colors",
  sizes: "variables-sizes",
  icons: "variables-icons",
};

const Dashboard = () => {
  const convex = useConvex();
  const { user, isAuthenticated, userProfile, signOut, loading: authLoading } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const hasEnsuredPublicIdsRef = useRef(false);
  const isFetchingProjectsRef = useRef(false);
  const isFetchingCraftspacesRef = useRef(false);
  const [projects, setProjects] = useState<ProjectWithPanorama[]>([]);
  const [craftspaceGroups, setCraftspaceGroups] = useState<CraftspaceGroup[]>([]);
  const [craftspaces, setCraftspaces] = useState<Craftspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createCraftspaceDialogOpen, setCreateCraftspaceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [renameDialogOpen, setRenameDialogOpen] = useState(false);
  const [projectToRename, setProjectToRename] = useState<Project | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameError, setRenameError] = useState<string | null>(null);
  const [renaming, setRenaming] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingCraftspace, setCreatingCraftspace] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [_refreshingUrls, setRefreshingUrls] = useState(false);
  const [_storageUsage, setStorageUsage] = useState<number>(0);
  const [selectedCraftspaceId, setSelectedCraftspaceId] = useState<string>("");
  const [showCraftspaceDropdown, setShowCraftspaceDropdown] = useState(false);
  const [activeCraftspaceId, setActiveCraftspaceId] = useState<string>("");

  const [s3ConnectionDialogOpen, setS3ConnectionDialogOpen] = useState(false);
  const [selectedStorageProvider, setSelectedStorageProvider] = useState<
    "local" | "360crafter" | "custom-s3"
  >(isElectron() ? "local" : "360crafter");
  const [pendingCraftspaceName, setPendingCraftspaceName] = useState<string>("");

  // Favicon preview state
  const [craftspaceNameInput, setCraftspaceNameInput] = useState("");
  const [faviconPreview, setFaviconPreview] = useState<string | null>(null);
  const [isFetchingFavicon, setIsFetchingFavicon] = useState(false);
  const [craftspaceSlugTaken, setCraftspaceSlugTaken] = useState(false);

  // Move craftspace functionality
  const [moveCraftspaceDialogOpen, setMoveCraftspaceDialogOpen] = useState(false);
  const [projectToMove, setProjectToMove] = useState<Project | null>(null);
  const [selectedTargetCraftspace, setSelectedTargetCraftspace] = useState<string>("");
  const [moving, setMoving] = useState(false);
  const [thumbnailProcessing, setThumbnailProcessing] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState<Set<string>>(new Set());
  const [s3ProviderName, setS3ProviderName] = useState<string>("");
  const [s3BucketName, setS3BucketName] = useState<string>("");
  const [ensuringDefaultCraftspace, setEnsuringDefaultCraftspace] = useState(false);

  const [activeView, setActiveView] = useState<DashboardView>("projects");
  const location = useLocation();

  const setActiveViewAndPersist = useCallback(
    (view: DashboardView) => {
      setActiveView(view);
      const params = new URLSearchParams(location.search);
      if (view === "projects") {
        params.delete("p");
      } else {
        params.set("p", viewToParam[view]);
      }
      const search = params.toString();
      navigate(
        { pathname: location.pathname, search: search ? `?${search}` : "" },
        { replace: true, state: location.state },
      );
    },
    [location.pathname, location.search, location.state, navigate],
  );

  // Project creation form state
  const [projectName, setProjectName] = useState("");
  const [projectSlug, setProjectSlug] = useState("");
  const [isSlugCustomized, setIsSlugCustomized] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    name: string | null;
    slug: string | null;
  }>({ name: null, slug: null });

  const liveCraftspaces = useQuery(api.craftspaces.listMine, user?.id ? {} : "skip") as
    | Craftspace[]
    | undefined;
  const liveProjects = useQuery(
    api.projects.listMineWithFirstPanorama,
    user?.id ? { limit: 50 } : "skip",
  ) as ProjectWithPanorama[] | undefined;

  // Local SQLite reactive subscriptions (only meaningful in Electron)
  const localCraftspaces = useLocalCraftspaces();
  const localProjects = useLocalProjects();

  const getDefaultCraftspaceId = () => activeCraftspaceId || craftspaces[0]?.id || "";

  // Initialize IndexedDB cache for project thumbnails
  const {
    getThumbnail,
    cacheThumbnailFromUrl,
    isInitialized: isCacheInitialized,
  } = useIndexedDBCache();

  // Fetch S3 provider and bucket name for the active craftspace
  const fetchS3ProviderInfo = useCallback(
    async (craftspaceId: string) => {
      try {
        const s3Connection = await convex.query(api.s3Connections.getByCraftspace, { craftspaceId });

        if (!s3Connection) {
          setS3ProviderName("");
          setS3BucketName("");
          return;
        }

        setS3ProviderName(s3Connection.provider);
        setS3BucketName(s3Connection.bucket_name);
      } catch (error) {
        console.error("Error fetching S3 provider info:", error);
        setS3ProviderName("");
        setS3BucketName("");
      }
    },
    [convex],
  );

  // Animation effect for project items
  useEffect(() => {
    if (projects.length > 0 && hasLoadedOnce) {
      // Show all projects with a small delay to ensure smooth animation
      setTimeout(() => {
        const allProjectIds = [...projects.map((p) => p.id), "new-project"];
        setVisibleProjects(new Set(allProjectIds));
      }, 100); // Small delay to ensure smooth animation
    } else {
      // Hide projects during loading or when no projects
      setVisibleProjects(new Set());
    }
  }, [projects, hasLoadedOnce]);

  // Fetch S3 provider info when active craftspace changes
  useEffect(() => {
    if (activeCraftspaceId) {
      const activeCraftspace = craftspaceGroups.find((g) => g.craftspaceId === activeCraftspaceId);
      if (activeCraftspace?.storageProvider === "custom-s3") {
        fetchS3ProviderInfo(activeCraftspaceId);
      } else {
        setS3ProviderName("");
        setS3BucketName("");
      }
    }
  }, [activeCraftspaceId, craftspaceGroups, fetchS3ProviderInfo]);

  // Get user subscription info
  const subscriptionInfo = userProfile ? getUserSubscriptionInfo(userProfile) : null;

  // Handle craftspace name input changes for URL detection
  const handleCraftspaceNameChange = async (value: string) => {
    setCraftspaceNameInput(value);
    setFaviconPreview(null);
    setCraftspaceSlugTaken(false);

    if (isUrl(value)) {
      setIsFetchingFavicon(true);
      try {
        const favicon = await getCraftspaceFavicon(value);
        setFaviconPreview(favicon);
      } catch (error) {
        console.warn("Failed to fetch favicon preview:", error);
      } finally {
        setIsFetchingFavicon(false);
      }
    }
  };

  const getCraftspaceSlugPreview = (value: string) => {
    const normalized = value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)+/g, "");
    return normalized || "craftspace";
  };

  // Reset form state when dialog closes
  const handleCreateCraftspaceDialogChange = (open: boolean) => {
    setCreateCraftspaceDialogOpen(open);
    if (!open) {
      setCraftspaceNameInput("");
      setFaviconPreview(null);
      setIsFetchingFavicon(false);
      setSelectedStorageProvider(isElectron() ? "local" : "360crafter");
      setCraftspaceSlugTaken(false);
    }
  };

  // Reset storage provider selection when subscription info changes
  useEffect(() => {
    if (subscriptionInfo) {
      if (selectedStorageProvider === "local") return;
      // If current selection is not allowed, reset to first available option
      if (
        selectedStorageProvider === "360crafter" &&
        !subscriptionInfo.canCreate360CrafterCraftspace
      ) {
        if (subscriptionInfo.canCreateBYOS3Craftspace) {
          setSelectedStorageProvider("custom-s3");
        }
      } else if (
        selectedStorageProvider === "custom-s3" &&
        !subscriptionInfo.canCreateBYOS3Craftspace
      ) {
        if (subscriptionInfo.canCreate360CrafterCraftspace) {
          setSelectedStorageProvider("360crafter");
        }
      }
    }
  }, [subscriptionInfo, selectedStorageProvider]);

  // User profile and subscription info loaded

  // Craftspace name mapping - now fetched from database
  const getCraftspaceName = useCallback(
    (craftspaceId: string): string => {
      const craftspace = craftspaces.find((w) => w.id === craftspaceId);
      const name = craftspace?.name?.trim();
      return name && name.length > 0 ? name : "Craftspace";
    },
    [craftspaces],
  );

  const getActiveCraftspaceName = (): string => {
    const activeCraftspace = craftspaces.find((w) => w.id === activeCraftspaceId);
    const activeName = activeCraftspace?.name?.trim();
    if (activeName && activeName.length > 0) return activeName;

    const groupedName = craftspaceGroups
      .find((g) => g.craftspaceId === activeCraftspaceId)
      ?.craftspaceName?.trim();
    if (groupedName && groupedName.length > 0) return groupedName;

    return "Your Projects";
  };

  const handleCreateCraftspace = () => {
    setCreateCraftspaceDialogOpen(true);
  };

  const groupProjectsByCraftspace = useCallback(
    (projects: ProjectWithPanorama[]): CraftspaceGroup[] => {
      const groups: Record<string, ProjectWithPanorama[]> = {};
      const fallbackCraftspaceId = activeCraftspaceId || craftspaces[0]?.id || "default";

      // Add all craftspaces (even empty ones)
      craftspaces.forEach((craftspace) => {
        groups[craftspace.id] = [];
      });

      // Group projects by craftspace (check both new craftspace_id and old workspace_id)
      projects.forEach((project) => {
        const projectAny = project as any;
        const craftspaceId = project.craftspace_id || projectAny.workspace_id || fallbackCraftspaceId;
        if (!groups[craftspaceId]) {
          groups[craftspaceId] = [];
        }
        groups[craftspaceId].push(project);
      });

      return Object.entries(groups).map(([craftspaceId, groupedProjects]) => {
        const craftspace = craftspaces.find((w) => w.id === craftspaceId);
        return {
          craftspaceId,
          craftspaceName: getCraftspaceName(craftspaceId),
          projects: groupedProjects.sort(
            (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
          ),
          storageProvider: craftspace?.storage_provider || "360crafter",
          _source: (craftspace as { _source?: "local" | "cloud" } | undefined)?._source,
        };
      });
    },
    [activeCraftspaceId, getCraftspaceName, craftspaces],
  );

  const syncCraftspaceState = useCallback(async (craftspacesToProcess: Craftspace[]) => {
    const availableCraftspaceIds = (craftspacesToProcess || []).map((w: Craftspace) => w.id);
    const recentCraftspaceId = getMostRecentCraftspace(
      (craftspacesToProcess || []).map((w: Craftspace) => ({ id: w.id })),
    );
    if (recentCraftspaceId && !availableCraftspaceIds.includes(recentCraftspaceId)) {
      console.log("🧹 Cleaning up reference to deleted craftspace:", recentCraftspaceId);
      const { clearRecentCraftspace } = await import("@/utils/craftspaceTracking");
      clearRecentCraftspace();
    }

    // Sort craftspaces by ownership first, then order (nulls last), then created_at
    const sortedCraftspaces = (craftspacesToProcess || []).sort((a, b) => {
      const aIsOwner = !!a.is_owner;
      const bIsOwner = !!b.is_owner;
      if (aIsOwner !== bIsOwner) {
        return aIsOwner ? -1 : 1;
      }

      const aHasOrder = typeof a.order === "number";
      const bHasOrder = typeof b.order === "number";
      if (aHasOrder && bHasOrder) {
        return (a.order as number) - (b.order as number);
      }
      if (aHasOrder && !bHasOrder) return -1;
      if (!aHasOrder && bHasOrder) return 1;
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

    setCraftspaces(sortedCraftspaces);
  }, []);

  const fetchProjects = useCallback(
    async function fetchProjectsInner(retryCount = 0) {
      if (isElectron()) {
        setLoading(false);
        return;
      }
      if (!user?.id) {
        setLoading(false);
        return;
      }
      if (isFetchingProjectsRef.current) {
        return;
      }
      isFetchingProjectsRef.current = true;

      // Circuit breaker - stop retrying after 3 attempts
      if (retryCount >= 3) {
        console.error("Maximum retry attempts reached, stopping fetchProjects");
        isFetchingProjectsRef.current = false;
        setLoading(false);
        return;
      }

      try {
        const projectsData = await convex.query(api.projects.listMineWithFirstPanorama, {
          limit: 50,
        });

        if (!projectsData || projectsData.length === 0) {
          setProjects([]);
          setCraftspaceGroups([]);
          setHasLoadedOnce(true);
          return;
        }

        const projectsWithPanoramas = projectsData as ProjectWithPanorama[];
        setProjects(projectsWithPanoramas);
        setCraftspaceGroups(groupProjectsByCraftspace(projectsWithPanoramas));
        setHasLoadedOnce(true);
      } catch (error) {
        console.error("Error fetching projects:", error);

        // Check if it's a resource exhaustion error and retry with smaller limits
        if (error.message?.includes("ERR_INSUFFICIENT_RESOURCES") && retryCount < 1) {
          console.log(`Retrying fetchProjects with smaller limits (attempt ${retryCount + 1})`);
          setTimeout(() => fetchProjectsInner(retryCount + 1), 2000); // 2 second delay
          return;
        }

        // Check for 406 errors and stop retrying
        if (error.message?.includes("406") || error.status === 406) {
          console.error("406 Not Acceptable error - stopping retries to prevent infinite loop");
          toast({
            variant: "destructive",
            title: "Database Error",
            description: "Unable to fetch project data. Please refresh the page.",
          });
          return;
        }

        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch projects. Please try refreshing the page.",
        });
      } finally {
        isFetchingProjectsRef.current = false;
        setLoading(false);
      }
    },
    [convex, groupProjectsByCraftspace, toast, user?.id],
  );

  const fetchStorageUsage = useCallback(() => {
    // For now, just set a placeholder value to avoid storage bucket errors
    // TODO: Implement proper storage usage calculation when bucket is available
    setStorageUsage(0.0);
  }, []);

  const fetchCraftspaces = useCallback(async () => {
    if (isElectron() || !user?.id) {
      return;
    }
    if (isFetchingCraftspacesRef.current) {
      return;
    }
    isFetchingCraftspacesRef.current = true;

    try {
      let craftspacesToProcess = await convex.query(api.craftspaces.listMine, {});

      if (!craftspacesToProcess || craftspacesToProcess.length === 0) {
        const defaultCraftspace = await convex.mutation(
          api.craftspaces.createDefaultCraftspaceIfMissing,
          {},
        );
        craftspacesToProcess = defaultCraftspace ? [defaultCraftspace] : [];
      }
      await syncCraftspaceState((craftspacesToProcess || []) as Craftspace[]);
    } catch (error) {
      console.error("Error fetching craftspaces:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch craftspaces",
      });
    } finally {
      isFetchingCraftspacesRef.current = false;
    }
  }, [convex, syncCraftspaceState, toast, user?.id]);

  const getProjectImageUrl = useCallback(
    (project: ProjectWithPanorama) => {
      const projectData = (project.data as Record<string, unknown> | null) ?? null;
      const socialImageUrl =
        typeof projectData?.socialImageUrl === "string" ? projectData.socialImageUrl.trim() : "";

      // Highest priority: explicit social image from Project Settings.
      if (socialImageUrl) {
        return socialImageUrl;
      }

      // Prefer an explicit project thumbnail if set by the user
      if (project.thumbnail_url) {
        return project.thumbnail_url;
      }

      const panorama = project.firstPanorama;
      if (!panorama) {
        return null;
      }

      // Check if panorama has a thumbnail URL (be more lenient with processing status)
      // For Crafter Cloud craftspaces, ensure we have signed URLs, not public URLs
      const needsSignedUrl = userProfile?.has_360crafter_cloud && !userProfile?.has_byo_s3;

      // Try WebP first, then fall back to JPEG
      if (panorama?.thumbnail_url_webp) {
        const url = panorama.thumbnail_url_webp;

        // If this is a Crafter Cloud craftspace and the URL is a public URL, convert to signed URL
        if (needsSignedUrl && url.includes("/object/public/")) {
          const path = url.split("/object/public/staging/")[1];
          if (path) {
            // This will be handled by the refreshThumbnailUrl function
            return null; // Force refresh
          }
        }
        return url;
      }

      if (panorama?.thumbnail_url) {
        const url = panorama.thumbnail_url;

        // If this is a Crafter Cloud craftspace and the URL is a public URL, convert to signed URL
        if (needsSignedUrl && url.includes("/object/public/")) {
          const path = url.split("/object/public/staging/")[1];
          if (path) {
            // This will be handled by the refreshThumbnailUrl function
            return null; // Force refresh
          }
        }
        return url;
      }

      return null;
    },
    [userProfile],
  );

  const handleCraftspaceSelect = async (craftspaceId: string) => {
    setActiveCraftspaceId(craftspaceId);
    setRecentCraftspace(craftspaceId);

    // Refresh URLs for the selected craftspace to ensure panoramas load properly
    try {
      const { refreshPrivateUrls } = await import("@/utils/refreshPrivateUrls");
      const result = await refreshPrivateUrls(user?.id || "");

      if (result.success && result.refreshedCount > 0) {
        // Reload projects to get updated URLs
        fetchProjects();
      }
    } catch (error) {
      console.error("❌ Failed to refresh URLs for craftspace:", error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const viewParam = params.get("p");
    const mappedView = viewParam ? paramToView[viewParam] : undefined;
    if (mappedView && dashboardViews.includes(mappedView) && mappedView !== activeView) {
      setActiveView(mappedView);
    }
  }, [location.search, activeView]);

  useEffect(() => {
    const state = location.state as { activeView?: DashboardView; craftspaceId?: string } | null;
    if (state?.activeView) {
      setActiveViewAndPersist(state.activeView);
    }
    if (state?.craftspaceId && state.craftspaceId !== activeCraftspaceId) {
      void handleCraftspaceSelect(state.craftspaceId);
    }
  }, [location.state, activeCraftspaceId, handleCraftspaceSelect, setActiveViewAndPersist]);

  const _handleRefreshUrls = async () => {
    if (!activeCraftspaceId) return;

    setRefreshingUrls(true);
    try {
      const result = await manualRefreshCraftspaceUrls(activeCraftspaceId);

      if (result.success) {
        toast({
          title: "URLs Refreshed",
          description: `Successfully refreshed ${result.refreshedCount} panorama URLs.`,
        });

        // Refresh the projects to get updated data
        await fetchProjects();
      } else {
        toast({
          variant: "destructive",
          title: "Refresh Failed",
          description: result.error || "Failed to refresh URLs",
        });
      }
    } catch (error) {
      console.error("Error refreshing URLs:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to refresh URLs",
      });
    } finally {
      setRefreshingUrls(false);
    }
  };

  const handleCraftspaceReorder = async (craftspaceId: string, newIndex: number) => {
    if (!user?.id) return;

    try {
      const draggedCraftspace = craftspaces.find((w) => w.id === craftspaceId);
      const targetCraftspace = craftspaces[newIndex];
      if (!draggedCraftspace?.is_owner || !targetCraftspace?.is_owner) {
        return;
      }

      const ownedCraftspaces = craftspaces.filter((w) => w.is_owner);
      const sharedCraftspaces = craftspaces.filter((w) => !w.is_owner);

      const draggedOwnedIndex = ownedCraftspaces.findIndex((w) => w.id === craftspaceId);
      const targetOwnedIndex = ownedCraftspaces.findIndex((w) => w.id === targetCraftspace.id);
      if (draggedOwnedIndex === -1 || targetOwnedIndex === -1) return;

      const reorderedOwned = [...ownedCraftspaces];
      const [movedCraftspace] = reorderedOwned.splice(draggedOwnedIndex, 1);
      reorderedOwned.splice(targetOwnedIndex, 0, movedCraftspace);

      // Update local state immediately for better UX
      setCraftspaces([...reorderedOwned, ...sharedCraftspaces]);

      // Update the order in the database for owned craftspaces only
      const updates = reorderedOwned.map((craftspace, index) => ({
        id: craftspace.id,
        order: index,
      }));

      await convex.mutation(api.craftspaces.reorder, { items: updates });
    } catch (error) {
      console.error("Error reordering craftspace:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to reorder craftspace",
      });
      // Revert local state on error
      fetchCraftspaces();
    }
  };

  useEffect(() => {
    if (user?.id) {
      // In Electron, data comes from local SQLite hooks — skip all Convex calls
      if (isElectron()) return;

      // Skip URL refresh in Dashboard - let Editor handle panorama URL management
      // This prevents unnecessary 400 errors for expired signed URLs

      // Ensure all accessible projects have legacy-style public IDs (prjxxxxxxxxx).
      if (!hasEnsuredPublicIdsRef.current) {
        hasEnsuredPublicIdsRef.current = true;
        void convex.mutation(api.projects.ensurePublicIds, {}).catch((error: unknown) => {
          console.warn("Failed to ensure project public IDs:", error);
          // Allow retry if the connection was closing.
          hasEnsuredPublicIdsRef.current = false;
        });
      }

      fetchProjects();
      fetchStorageUsage();
    }
  }, [convex, user?.id, fetchProjects, fetchStorageUsage]);

  useEffect(() => {
    if (isElectron() || !user?.id || liveCraftspaces === undefined) return;

    const syncFromSubscription = async () => {
      if (liveCraftspaces.length === 0 && !ensuringDefaultCraftspace) {
        try {
          setEnsuringDefaultCraftspace(true);
          await convex.mutation(api.craftspaces.createDefaultCraftspaceIfMissing, {});
        } catch (error) {
          console.error("Error ensuring default craftspace:", error);
        } finally {
          setEnsuringDefaultCraftspace(false);
        }
        return;
      }

      await syncCraftspaceState(liveCraftspaces);
      // Craftspace memberships can change live; refresh projects immediately so
      // newly shared craftspace projects appear without a manual page refresh.
      fetchProjects();
    };

    syncFromSubscription();
  }, [
    user?.id,
    liveCraftspaces,
    ensuringDefaultCraftspace,
    convex,
    syncCraftspaceState,
    fetchProjects,
  ]);

  useEffect(() => {
    if (isElectron() || !user?.id || liveProjects === undefined) return;

    setProjects(liveProjects);
    setHasLoadedOnce(true);
    setLoading(false);
  }, [user?.id, liveProjects]);

  // Electron: sync local SQLite craftspaces + cloud craftspaces (when signed in) reactively
  useEffect(() => {
    if (!isElectron() || localCraftspaces === undefined) return;
    const localMapped = localCraftspaces.map((lw) => ({
      id: lw._id,
      name: lw.name,
      created_at: lw.created_at ?? new Date().toISOString(),
      storage_provider: lw.storage_provider,
      icon_url: lw.icon_url ?? null,
      order: lw.order ?? null,
      is_owner: true,
      access_role: "owner" as const,
      _source: "local" as const,
    }));
    // When signed in, also show cloud craftspaces from Convex alongside local ones
    const cloudMapped = (liveCraftspaces ?? []).map((w) => ({
      ...w,
      _source: "cloud" as const,
    }));
    syncCraftspaceState([...localMapped, ...cloudMapped] as unknown as Craftspace[]);
  }, [localCraftspaces, liveCraftspaces, syncCraftspaceState]);

  // Electron: sync local SQLite projects + cloud projects (when signed in) reactively
  useEffect(() => {
    if (!isElectron() || localProjects === undefined) return;
    const localMapped = localProjects.map((lp) => ({
      ...lp,
      id: lp._id,
      firstPanorama: lp.first_panorama_thumbnail
        ? {
            thumbnail_url: lp.first_panorama_thumbnail.startsWith("app://")
              ? lp.first_panorama_thumbnail
              : `app://storage/${lp.first_panorama_thumbnail}`,
          }
        : undefined,
    })) as unknown as ProjectWithPanorama[];
    // When signed in, also include cloud projects alongside local ones
    const cloudProjects = (liveProjects ?? []) as ProjectWithPanorama[];
    setProjects([...localMapped, ...cloudProjects]);
    setHasLoadedOnce(true);
    setLoading(false);
  }, [localProjects, liveProjects]);

  // Check if cache is empty and trigger refresh if needed
  useEffect(() => {
    if (isCacheInitialized && projects.length > 0 && !loading) {
      const checkCacheAndRefresh = async () => {
        try {
          // Check if we have any cached thumbnails
          let hasCachedThumbnails = false;
          let hasValidUrls = false;

          for (const project of projects) {
            if (project.firstPanorama?.id) {
              const cachedThumbnail = await getThumbnail(project.firstPanorama.id, true); // Check WebP first
              if (cachedThumbnail) {
                hasCachedThumbnails = true;
                break;
              }
              const cachedThumbnailJpeg = await getThumbnail(project.firstPanorama.id, false); // Check JPEG
              if (cachedThumbnailJpeg) {
                hasCachedThumbnails = true;
                break;
              }
            }

            // Also check if we have valid network URLs
            const imageUrl = getProjectImageUrl(project);
            if (imageUrl) {
              hasValidUrls = true;
            }
          }

          // If no cached thumbnails found, refresh project data to get fresh URLs
          if (!hasCachedThumbnails) {
            // No cached thumbnails found, refreshing project data
            await fetchProjects();
          }

          // If we have no valid URLs at all, this might indicate a data issue
          if (!hasValidUrls && !hasCachedThumbnails) {
            console.log("⚠️ No valid thumbnail URLs found, this might indicate a data issue");
          }
        } catch (error) {
          console.warn("Failed to check cache status:", error);
        }
      };

      checkCacheAndRefresh();
    }
  }, [isCacheInitialized, projects, loading, getThumbnail, getProjectImageUrl, fetchProjects]);

  // Update craftspace groups whenever craftspaces or projects change.
  // In Electron, this must also run when there's no logged-in user (local-only mode).
  useEffect(() => {
    if (!user?.id && !isElectron()) return;

    const groups = groupProjectsByCraftspace(projects);
    setCraftspaceGroups(groups);

    // Check if current activeCraftspaceId is valid, if not, switch to another available craftspace
    if (groups.length > 0) {
      const isCurrentCraftspaceValid =
        activeCraftspaceId && groups.some((g) => g.craftspaceId === activeCraftspaceId);

      if (!isCurrentCraftspaceValid || !activeCraftspaceId) {
        // Try to get the most recently used craftspace first
        const mostRecentCraftspaceId = getMostRecentCraftspace(
          groups.map((g) => ({ id: g.craftspaceId })),
        );

        if (
          mostRecentCraftspaceId &&
          groups.some((g) => g.craftspaceId === mostRecentCraftspaceId)
        ) {
          setActiveCraftspaceId(mostRecentCraftspaceId);
          setRecentCraftspace(mostRecentCraftspaceId);
          if (user?.id) fetchCraftspaces();
        } else {
          setActiveCraftspaceId(groups[0].craftspaceId);
          setRecentCraftspace(groups[0].craftspaceId);
          if (user?.id) fetchCraftspaces();
        }
      }
    }
  }, [projects, user?.id, activeCraftspaceId, fetchCraftspaces, groupProjectsByCraftspace]);

  // Auto-refresh projects every 30 seconds with smart URL refresh
  useEffect(() => {
    if (!user?.id) return;

    const interval = setInterval(async () => {
      // Check if we have any panoramas that actually need processing (not compressed ones with failed status)
      const hasUnprocessedPanoramas = craftspaceGroups.some((group) =>
        group.projects.some((project) => {
          const panorama = project.firstPanorama;
          if (!panorama) return false;

          // Skip if already compressed (these don't need reprocessing)
          if (
            panorama.name?.includes("compressed_") ||
            panorama.private_file_url?.includes("compressed_") ||
            panorama.file_url?.includes("compressed_")
          ) {
            return false;
          }

          // Only consider truly pending/failed uncompressed panoramas
          return (
            panorama.processing_status === "pending" || panorama.processing_status === "processing"
          );
        }),
      );

      // Check for potentially expired URLs and refresh proactively
      let hasExpiredUrls = false;
      craftspaceGroups.forEach((group) => {
        group.projects.forEach((project) => {
          const imageUrl = getProjectImageUrl(project);
          if (imageUrl && (imageUrl.includes("X-Amz-Expires") || imageUrl.includes("expires"))) {
            try {
              const url = new URL(imageUrl);
              const expiresParam = url.searchParams.get("X-Amz-Expires");
              const dateParam = url.searchParams.get("X-Amz-Date");

              if (expiresParam && dateParam) {
                const expirationTime = new Date(dateParam);
                expirationTime.setSeconds(expirationTime.getSeconds() + parseInt(expiresParam, 10));
                const now = new Date();
                const timeUntilExpiry = expirationTime.getTime() - now.getTime();

                // If URL expires within next 10 minutes, mark for refresh
                if (timeUntilExpiry < 10 * 60 * 1000) {
                  hasExpiredUrls = true;
                }
              }
            } catch {
              // Ignore URL parsing errors
            }
          }
        });
      });

      if (hasUnprocessedPanoramas) {
        // Only refresh if we have genuinely unprocessed panoramas
        // Refreshing for unprocessed panoramas
        fetchProjects();
      } else if (hasExpiredUrls) {
        // Refresh to get new signed URLs
        // Refreshing for expired URLs
        fetchProjects();
      } else {
        // Normal refresh every 30 seconds for other updates
        fetchProjects();
      }
    }, 30000); // Always use 30 seconds for now to avoid complexity

    return () => clearInterval(interval);
  }, [user?.id, craftspaceGroups, fetchProjects, getProjectImageUrl]);

  // Close craftspace dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".craftspace-dropdown")) {
        setShowCraftspaceDropdown(false);
      }
    };

    if (showCraftspaceDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showCraftspaceDropdown]);

  // Attempt insert; on name uniqueness error (23505), auto-suffix and retry
  const insertProjectWithUniqueName = async (
    baseName: string,
    description: string,
    craftspaceId: string,
    customSlug?: string,
    useLocalProject?: boolean,
  ) => {
    const trimmed = baseName?.trim() || "New Project";
    let attempt = 0;

    // Get craftspace favicon to inherit as default
    let craftspaceFavicon: string | null = null;
    let existingCraftspaceProjects: ProjectWithPanorama[] = [];

    if (useLocalProject) {
      // In Electron, use already-loaded local state — no Convex calls
      const localWs = craftspaces.find((w) => w.id === craftspaceId);
      craftspaceFavicon = localWs?.icon_url || null;
      existingCraftspaceProjects = projects.filter((p) => p.craftspace_id === craftspaceId);
    } else {
      try {
        const craftspace = await convex.query(api.craftspaces.get, { id: craftspaceId });
        craftspaceFavicon = craftspace?.icon_url || null;
      } catch (error) {
        console.warn("Failed to fetch craftspace favicon for inheritance:", error);
      }

      try {
        const existing = await convex.query(api.projects.listByCraftspace, { craftspaceId });
        existingCraftspaceProjects = (existing || []) as ProjectWithPanorama[];
      } catch (error) {
        console.warn("Failed to load craftspace projects for uniqueness check:", error);
      }
    }

    // Try up to 20 unique suffixes
    // "Name", "Name 2", "Name 3", ...
    // Stop retrying on non-23505 errors
    while (attempt < 20) {
      const candidate = attempt === 0 ? trimmed : `${trimmed} ${attempt + 1}`;

      const hasDuplicate = existingCraftspaceProjects.some(
        (project) => project.name.toLowerCase() === candidate.toLowerCase(),
      );
      if (hasDuplicate) {
        attempt += 1;
        continue;
      }

      // Initialize project data with craftspace favicon inheritance
      const projectDataObj: Record<string, unknown> = {};

      // Add custom slug to project data if provided
      if (customSlug?.trim()) {
        projectDataObj.customSlug = customSlug.trim();
      }

      // Add craftspace favicon inheritance info
      if (craftspaceFavicon) {
        projectDataObj.inheritedCraftspaceFavicon = craftspaceFavicon;
        projectDataObj.faviconSource = "craftspace";
      }

      if (useLocalProject) {
        const projectId = await createLocalProject({
          name: candidate,
          description,
          craftspace_id: craftspaceId,
          data: Object.keys(projectDataObj).length > 0 ? projectDataObj : undefined,
        });
        return {
          data: { id: projectId, craftspace_id: craftspaceId } as unknown as ProjectWithPanorama,
          name: candidate,
        };
      }

      const data = await convex.mutation(api.projects.create, {
        name: candidate,
        description,
        craftspace_id: craftspaceId,
        data: Object.keys(projectDataObj).length > 0 ? projectDataObj : undefined,
      });

      return { data, name: candidate };
    }
    throw new Error("Could not create a unique project name");
  };

  const _generateMissingThumbnails = async () => {
    if (!user?.id || thumbnailProcessing) {
      return;
    }

    setThumbnailProcessing(true);

    try {
      // Checking for panoramas without thumbnails

      // Check for panoramas that need thumbnail generation (limit to prevent resource exhaustion)
      const panoramas = await convex.query(api.panoramas.listNeedsThumbnails, { limit: 20 });

      if (!panoramas || panoramas.length === 0) {
        // All panoramas already have thumbnails
        return;
      }

      // Found panoramas that need thumbnail generation

      // Trigger reprocessing for panoramas without thumbnails
      const result = await reprocessPanoramas();

      if (result.success) {
        const summary = result.summary;

        if (summary && summary.processed > 0) {
          // Some panoramas were actually processed
          toast({
            title: "Thumbnail Generation Started",
            description: `Processing ${summary.processed} panoramas${summary.skipped > 0 ? ` (skipped ${summary.skipped} already compressed)` : ""}. This may take a few minutes.`,
          });

          // Refresh projects to show updated thumbnails
          setTimeout(() => {
            fetchProjects();
          }, 2000); // Give some time for processing to start
        } else if (summary && summary.skipped > 0) {
          // All panoramas were skipped (already compressed/completed)
          toast({
            title: "No Processing Needed",
            description: `All ${summary.skipped} panoramas are already processed. No thumbnail generation required.`,
          });
        } else {
          // No panoramas found at all
          toast({
            title: "No Panoramas Found",
            description: "No panoramas need thumbnail generation.",
          });
        }
      } else {
        console.error("❌ Failed to initiate thumbnail generation:", result.error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to generate thumbnails. Please try again.",
        });
      }
    } catch (error) {
      console.error("Error generating missing thumbnails:", error);
    } finally {
      setThumbnailProcessing(false);
    }
  };

  const createProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreating(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;
    const description = formData.get("description") as string;
    const slug = formData.get("slug") as string;

    try {
      const craftspaceId = selectedCraftspaceId || getDefaultCraftspaceId();
      if (!craftspaceId) {
        throw new Error("No craftspace available for project creation");
      }
      const craftspace = craftspaces.find((w) => w.id === craftspaceId);
      const useLocalProject = isElectron() && craftspace?._source !== "cloud";

      // Create project with custom slug if provided
      const { data, name: createdName } = await insertProjectWithUniqueName(
        name,
        description,
        craftspaceId,
        slug,
        useLocalProject,
      );

      // Create project folders in storage (best effort; don't block project creation)
      if (!useLocalProject) {
        try {
          await createProjectFolders(craftspaceId, data.id);
        } catch (folderError) {
          console.warn("Non-blocking: failed to create project folders", folderError);
        }
      }

      // Initialize default CMS collections (best effort; don't block navigation)
      CMSService.initializeDefaultCollections(data.id, useLocalProject).catch((err) => {
        console.warn("Non-blocking: failed to initialize default CMS collections", err);
      });

      setProjects([data, ...projects]);
      setCraftspaceGroups(groupProjectsByCraftspace([data, ...projects]));
      setCreateDialogOpen(false);
      setSelectedCraftspaceId(""); // Reset selection
      resetProjectForm(); // Reset form state

      // Navigate to the newly created project
      navigate(`/${data.id}`);
    } catch (error) {
      console.error("Error creating project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create project",
      });
    } finally {
      setCreating(false);
    }
  };

  const createCraftspace = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setCreatingCraftspace(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get("name") as string;

    try {
      // Local craftspace creation (Electron)
      if (isElectron() && selectedStorageProvider === "local") {
        let iconUrl: string | undefined;
        try {
          const url = await getCraftspaceFavicon(name);
          if (url) iconUrl = url;
        } catch {
          // favicon is optional
        }
        await createLocalCraftspace({ name, icon_url: iconUrl, storage_provider: "360crafter" });
        setCreateCraftspaceDialogOpen(false);
        setCraftspaceNameInput("");
        setSelectedStorageProvider("local");
        toast({ title: "Craftspace created", description: `${name} created with local storage.` });
        setCreatingCraftspace(false);
        return;
      }

      // Cloud craftspace creation requires authentication
      if (!user?.id) {
        toast({
          variant: "destructive",
          title: "Sign in required",
          description: "Sign in to create Crafter Cloud or BYO S3 craftspaces.",
        });
        setCreatingCraftspace(false);
        return;
      }

      // Check permissions before creating craftspace
      if (!subscriptionInfo) {
        throw new Error("Unable to verify subscription status");
      }

      if (
        selectedStorageProvider === "360crafter" &&
        !subscriptionInfo.canCreate360CrafterCraftspace
      ) {
        toast({
          variant: "destructive",
          title: "Subscription Required",
          description: getUpgradeMessage("360crafter_cloud").description,
        });
        setCreatingCraftspace(false);
        return;
      }

      if (selectedStorageProvider === "custom-s3" && !subscriptionInfo.canCreateBYOS3Craftspace) {
        toast({
          variant: "destructive",
          title: "Access Required",
          description: getUpgradeMessage("byo_s3").description,
        });
        setCreatingCraftspace(false);
        return;
      }

      // Try to fetch favicon if craftspace name looks like a URL
      let iconUrl: string | null = null;
      try {
        iconUrl = await getCraftspaceFavicon(name);
      } catch (error) {
        console.warn("Failed to fetch favicon for craftspace:", error);
        // Continue without favicon - this is not a critical error
      }

      if (selectedStorageProvider === "custom-s3") {
        setCreateCraftspaceDialogOpen(false);
        setPendingCraftspaceName(name);
        setS3ConnectionDialogOpen(true);
        setCreatingCraftspace(false);
        return;
      }

      const createdCraftspace = await convex.mutation(api.craftspaces.create, {
        name,
        storage_provider: selectedStorageProvider,
        icon_url: iconUrl ?? undefined,
      });

      // Create craftspace folders in storage (non-blocking)
      try {
        await createCraftspaceFolders(createdCraftspace.id);
      } catch (folderError) {
        console.warn(
          "Failed to create craftspace folders, but craftspace was created successfully:",
          folderError,
        );
        // Don't fail the craftspace creation if folder creation fails
        // Folders will be created when needed
      }

      setCraftspaces([...craftspaces, createdCraftspace as Craftspace]);

      setCreateCraftspaceDialogOpen(false);
      setSelectedStorageProvider(isElectron() ? "local" : "360crafter"); // Reset to default
      toast({
        title: "Craftspace created!",
        description: `${name} has been created successfully with Crafter Cloud storage.`,
      });
    } catch (error) {
      console.error("Error creating craftspace:", error);
      const message = error?.message ?? "";
      if (message.includes("UNIQUE constraint failed: craftspaces.slug")) {
        setCraftspaceSlugTaken(true);
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to create craftspace",
        });
      }
    } finally {
      setCreatingCraftspace(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      // If it's a session missing error, still navigate to auth page
      if (error.message?.includes("Auth session missing")) {
        navigate("/login");
        return;
      }
      toast({
        variant: "destructive",
        title: "Sign out failed",
        description: "There was an error signing out. Please try again.",
      });
    }
  };

  const _handleS3ConnectionCancel = async () => {
    setPendingCraftspaceName("");
    setSelectedStorageProvider("360crafter");
    setCreatingCraftspace(false);
    setS3ConnectionDialogOpen(false);
  };

  const handleS3ConnectionComplete = async (connectionData: {
    craftspaceName: string;
    provider: string;
    bucketName: string;
    region: string;
    endpoint: string;
    accessKeyId?: string;
    secretAccessKey?: string;
  }) => {
    try {
      const craftspaceName = pendingCraftspaceName || connectionData.craftspaceName;
      if (!craftspaceName) {
        throw new Error("Craftspace name is required");
      }

      if (!connectionData.accessKeyId || !connectionData.secretAccessKey) {
        throw new Error("Missing S3 credentials");
      }

      // Try to fetch favicon if craftspace name looks like a URL
      let iconUrl: string | null = null;
      try {
        iconUrl = await getCraftspaceFavicon(craftspaceName);
      } catch (error) {
        console.warn("Failed to fetch favicon for craftspace:", error);
      }

      const createdCraftspace = await convex.mutation(api.craftspaces.create, {
        name: craftspaceName,
        storage_provider: "custom-s3",
        s3_bucket_name: connectionData.bucketName,
        s3_region: connectionData.region,
        s3_endpoint: connectionData.endpoint,
        icon_url: iconUrl ?? undefined,
      });

      await EncryptedS3CredentialService.storeCredentials(createdCraftspace.id, {
        accessKeyId: connectionData.accessKeyId,
        secretAccessKey: connectionData.secretAccessKey,
        endpoint: connectionData.endpoint,
        region: connectionData.region,
        bucketName: connectionData.bucketName,
        useCorsProxy: false,
      });

      // Create craftspace folders in storage (after S3 connection is saved).
      await createCraftspaceFolders(createdCraftspace.id);

      await convex.mutation(api.s3Connections.upsert, {
        craftspace_id: createdCraftspace.id,
        name: craftspaceName,
        provider: connectionData.provider,
        bucket_name: connectionData.bucketName,
        region: connectionData.region,
        endpoint: connectionData.endpoint,
      });

      await fetchCraftspaces();

      setS3ConnectionDialogOpen(false);
      setPendingCraftspaceName("");
      setSelectedStorageProvider("360crafter");
      setCreatingCraftspace(false);

      toast({
        title: "Craftspace created!",
        description: `${craftspaceName} has been created successfully with custom S3 storage.`,
      });
    } catch (error) {
      console.error("Error creating craftspace with S3:", error);

      setS3ConnectionDialogOpen(false);
      setPendingCraftspaceName("");
      setSelectedStorageProvider("360crafter");
      setCreatingCraftspace(false);

      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create craftspace with S3 connection",
      });
    }
  };

  const openProject = (projectId: string) => {
    // Save the current craftspace context before navigating
    if (activeCraftspaceId) {
      setRecentCraftspace(activeCraftspaceId);
    }
    navigate(`/${projectId}`);
  };

  const duplicateProject = async (project: Project) => {
    try {
      const data = await convex.mutation(api.projects.duplicate, { id: project.id });

      setProjects([data, ...projects]);
      setCraftspaceGroups(groupProjectsByCraftspace([data, ...projects]));
      toast({
        title: "Project duplicated!",
        description: `${project.name} has been duplicated successfully.`,
      });
    } catch (error) {
      console.error("Error duplicating project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to duplicate project",
      });
    }
  };

  const handleDelete = (project: Project) => {
    setProjectToDelete(project);
    setDeleteConfirmation("");
    setDeleteDialogOpen(true);
  };

  const handleRenameOpen = (project: Project) => {
    setProjectToRename(project);
    setRenameValue(project.name);
    setRenameError(null);
    setRenameDialogOpen(true);
  };

  const validateRenameName = (name: string, project: Project | null) => {
    if (!project) return "Project not found";
    const trimmed = name.trim();
    if (!trimmed) return "Project name is required";

    const craftspaceId = project.craftspace_id || getDefaultCraftspaceId();
    const duplicate = projects.find(
      (p) =>
        p.id !== project.id &&
        p.craftspace_id === craftspaceId &&
        p.name.toLowerCase() === trimmed.toLowerCase(),
    );
    if (duplicate) {
      return "A project with this name already exists in this craftspace";
    }

    return null;
  };

  const confirmRename = async () => {
    if (!projectToRename) return;
    const nextName = renameValue.trim();
    const error = validateRenameName(nextName, projectToRename);
    if (error) {
      setRenameError(error);
      return;
    }

    setRenaming(true);
    try {
      if (isElectron()) {
        await updateLocalProject({
          id: projectToRename.id,
          craftspace_id: projectToRename.craftspace_id || "default",
          name: nextName,
        });
      } else {
        await convex.mutation(api.projects.update, { id: projectToRename.id, name: nextName });
      }

      const updatedProjects = projects.map((p) =>
        p.id === projectToRename.id ? { ...p, name: nextName } : p,
      );
      setProjects(updatedProjects);
      setCraftspaceGroups(groupProjectsByCraftspace(updatedProjects));

      setRenameDialogOpen(false);
      setProjectToRename(null);
      toast({
        title: "Project renamed",
        description: `Renamed to "${nextName}".`,
      });
    } catch (error) {
      console.error("Error renaming project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to rename project",
      });
    } finally {
      setRenaming(false);
    }
  };

  const handleMoveProject = (project: Project) => {
    setProjectToMove(project);
    setSelectedTargetCraftspace("");
    setMoveCraftspaceDialogOpen(true);
  };

  const moveProject = async () => {
    if (!projectToMove || !selectedTargetCraftspace) return;

    setMoving(true);
    try {
      if (isElectron()) {
        await moveLocalProjectToCraftspace({
          id: projectToMove.id,
          sourceCraftspaceId: projectToMove.craftspace_id || "default",
          targetCraftspaceId: selectedTargetCraftspace,
        });
      } else {
        await convex.mutation(api.projects.update, {
          id: projectToMove.id,
          craftspace_id: selectedTargetCraftspace,
        });
      }

      // Update local state
      const updatedProjects = projects.map((p) =>
        p.id === projectToMove.id ? { ...p, craftspace_id: selectedTargetCraftspace } : p,
      );
      setProjects(updatedProjects);
      setCraftspaceGroups(groupProjectsByCraftspace(updatedProjects));

      setMoveCraftspaceDialogOpen(false);
      setProjectToMove(null);
      setSelectedTargetCraftspace("");

      toast({
        title: "Project moved",
        description: `${projectToMove.name} has been moved successfully.`,
      });
    } catch (error) {
      console.error("Error moving project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to move project",
      });
    } finally {
      setMoving(false);
    }
  };

  const confirmDelete = async () => {
    if (!projectToDelete || deleteConfirmation !== projectToDelete.name) return;

    setDeleting(true);
    try {
      const projectData = projectToDelete.data as Record<string, unknown> | null;
      const craftspaceSlug =
        craftspaces.find((w) => w.id === projectToDelete.craftspace_id)?.slug || null;
      const storageSegments = resolveStoragePathSegments({
        craftspaceId: projectToDelete.craftspace_id || "default",
        projectId: projectToDelete.id,
        projectName: projectToDelete.name,
        projectData,
        userEmail: user?.email,
        userId: user?.id,
        workspaceSlug: craftspaceSlug,
        storagePath: typeof projectData?.storage_path === "string" ? projectData.storage_path : null,
      });
      // Use comprehensive deletion with storage cleanup
      const result = await deleteProjectCompletely(
        projectToDelete.id,
        projectToDelete.craftspace_id || "default",
        storageSegments,
      );

      if (!result.success) {
        throw new Error(result.error || "Failed to delete project");
      }

      const updatedProjects = projects.filter((p) => p.id !== projectToDelete.id);
      setProjects(updatedProjects);
      setCraftspaceGroups(groupProjectsByCraftspace(updatedProjects));
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
      setDeleteConfirmation("");

      toast({
        title: "Project deleted!",
        description: `${projectToDelete.name} has been deleted successfully. ${result.deletedFiles} files removed from storage.`,
      });
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete project",
      });
    } finally {
      setDeleting(false);
    }
  };

  // Track failed refresh attempts to prevent infinite loops
  const failedRefreshAttempts = useRef<Set<string>>(new Set());

  // Function to refresh expired URLs by generating new signed URLs or updating the database
  const _refreshThumbnailUrl = async (projectId: string): Promise<string | null> => {
    // Check if we've already failed to refresh this project recently
    if (failedRefreshAttempts.current.has(projectId)) {
      return null;
    }

    try {
      // Reduced logging - only log in development
      if (process.env.NODE_ENV === "development") {
        // Refreshing thumbnail URL for project
      }

      const project = await convex.query(api.projects.get, { id: projectId });
      if (!project) {
        return null;
      }

      const panoramas = await convex.query(api.panoramas.listByProject, { projectId });

      const panorama = (panoramas || []).find((item) => item.processing_status === "completed");
      if (!panorama) {
        return null;
      }

      const craftspaceId = project.craftspace_id;
      const needsSignedUrl = userProfile?.has_360crafter_cloud && !userProfile?.has_byo_s3;

      // If this is a Crafter Cloud craftspace, generate new signed URLs
      if (needsSignedUrl && panorama.thumbnail_url) {
        // Check if current URL is a public URL that should be signed
        if (panorama.thumbnail_url.includes("/object/public/")) {
          const path = panorama.thumbnail_url.split("/object/public/staging/")[1];
          if (path) {
            // Generate new signed URL
            const { data: signedData } = await StorageService.getPrivateUrl("staging", path, 3600);

            if (signedData?.signedUrl) {
              await convex.mutation(api.panoramas.update, {
                id: panorama.id,
                thumbnail_url: signedData.signedUrl,
              });
              return signedData.signedUrl;
            } else {
              console.error("Error generating signed URL for thumbnail path:", path);
            }
          }
        } else {
          // URL might be expired signed URL, try refreshing via storage service
          try {
            const storageService = await StorageServiceFactory.createStorageService(craftspaceId);
            const path = panorama.thumbnail_url.includes("/staging/")
              ? panorama.thumbnail_url.split("/staging/")[1]?.split("?")[0]
              : null;

            if (path) {
              const newUrl = await storageService.getFileUrl(path);
              await convex.mutation(api.panoramas.update, {
                id: panorama.id,
                thumbnail_url: newUrl,
              });
              return newUrl;
            }
          } catch (storageError) {
            console.error("Error refreshing via storage service:", storageError);
          }
        }
      }

      // Return existing URL if no refresh was needed or possible
      return panorama.thumbnail_url;
    } catch (error) {
      console.error("Error refreshing thumbnail URL:", error);
      // Mark this project as failed to prevent infinite retries
      failedRefreshAttempts.current.add(projectId);

      // Clear the failed attempt after 5 minutes to allow retry
      setTimeout(
        () => {
          failedRefreshAttempts.current.delete(projectId);
        },
        5 * 60 * 1000,
      );

      return null;
    }
  };

  // Track last refresh time to prevent too frequent refreshes
  const lastRefreshTime = useRef<number>(0);
  const REFRESH_THROTTLE_MS = 60000; // 60 seconds - increased throttle
  const refreshInProgress = useRef<boolean>(false);

  // Function to automatically refresh expired thumbnails in the background
  const autoRefreshExpiredThumbnails = useCallback(async () => {
    if (isElectron()) return; // Thumbnails are local files — no Convex URL refresh needed
    const now = Date.now();
    if (now - lastRefreshTime.current < REFRESH_THROTTLE_MS || refreshInProgress.current) {
      return; // Throttle refreshes and prevent concurrent runs
    }
    lastRefreshTime.current = now;
    refreshInProgress.current = true;

    try {
      const updatedProjects = await convex.query(api.projects.listMineWithFirstPanorama, {
        limit: 50,
      });

      setProjects(updatedProjects || []);
      setCraftspaceGroups(groupProjectsByCraftspace(updatedProjects || []));

      // console.log(`✅ Auto-refreshed ${updatedProjects.length} project thumbnails`);
    } catch (error) {
      console.error("Error auto-refreshing thumbnails:", error);
    } finally {
      refreshInProgress.current = false;
    }
  }, [convex, groupProjectsByCraftspace]);

  // Set up automatic thumbnail refresh every 6 hours (since URLs now expire in 1 day)
  useEffect(() => {
    const interval = setInterval(autoRefreshExpiredThumbnails, 6 * 60 * 60 * 1000); // 6 hours

    return () => clearInterval(interval);
  }, [autoRefreshExpiredThumbnails]);

  // Only refresh thumbnails when the component mounts, not on every project change
  useEffect(() => {
    if (projects.length > 0 && lastRefreshTime.current === 0) {
      autoRefreshExpiredThumbnails();
    }
  }, [projects.length, autoRefreshExpiredThumbnails]);

  // Function to check S3 URL status and provide debugging info
  const _debugThumbnailUrl = async (url: string, projectName: string) => {
    try {
      // console.log(`🔍 Debugging thumbnail URL for ${projectName}:`, url);

      // Check if URL is expired by parsing the expiration time
      const urlParams = new URLSearchParams(url.split("?")[1]);
      const expiresParam = urlParams.get("X-Amz-Expires");
      const dateParam = urlParams.get("X-Amz-Date");

      if (expiresParam && dateParam) {
        const expirationTime = new Date(dateParam);
        expirationTime.setSeconds(expirationTime.getSeconds() + parseInt(expiresParam, 10));
        const now = new Date();
        const isExpired = now > expirationTime;

        // URL expiration info logging commented out for cleaner console

        if (isExpired) {
          console.warn(`⚠️ S3 URL for ${projectName} has expired!`);
          toast({
            variant: "destructive",
            title: "URL Expired",
            description: `Thumbnail URL for ${projectName} has expired. Please refresh the thumbnails.`,
          });
        }
      }

      // Try to fetch the URL to check if it's accessible
      // Skip problematic S3 URLs to prevent 403 errors
      if (url.includes("crafter.learn.360creators.com") && url.includes("thumbnail_")) {
        console.log("⏭️ Skipping known problematic S3 URL in Dashboard check:", url);
        return;
      }

      try {
        const response = await fetch(url, { method: "HEAD" });
        // console.log(`🌐 HTTP status for ${projectName}:`, response.status, response.statusText);

        if (!response.ok) {
          console.error(`❌ HTTP error for ${projectName}:`, response.status, response.statusText);
        }
      } catch (fetchError) {
        console.error(`❌ Fetch error for ${projectName}:`, fetchError);
      }
    } catch (error) {
      console.error(`❌ Error debugging URL for ${projectName}:`, error);
    }
  };

  // Generate slug from project name
  const generateSlug = (name: string) => {
    return name.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase();
  };

  // Handle project name change
  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.value;
    setProjectName(name);

    // Only auto-generate slug if user hasn't customized it
    if (!isSlugCustomized) {
      setProjectSlug(generateSlug(name));
    }

    // Validate in real-time
    const slug = isSlugCustomized ? projectSlug : generateSlug(name);
    const craftspaceId = selectedCraftspaceId || getDefaultCraftspaceId();
    const duplicates = checkForDuplicates(name, slug, craftspaceId);
    setValidationErrors(duplicates);
  };

  // Handle slug change
  const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const slug = e.target.value;
    setProjectSlug(slug);
    setIsSlugCustomized(true);

    // Validate in real-time
    const name = projectName.trim();
    const craftspaceId = selectedCraftspaceId || getDefaultCraftspaceId();
    const duplicates = checkForDuplicates(name, slug, craftspaceId);
    setValidationErrors(duplicates);
  };

  // Reset slug customization when project name is cleared
  const handleProjectNameBlur = () => {
    if (!projectName.trim()) {
      setProjectSlug("");
      setIsSlugCustomized(false);
    }
  };

  // Reset project creation form
  const resetProjectForm = () => {
    setProjectName("");
    setProjectSlug("");
    setIsSlugCustomized(false);
    setValidationErrors({ name: null, slug: null });
  };

  // Check for duplicate project names and slugs in the same craftspace
  const checkForDuplicates = (
    name: string,
    slug: string,
    craftspaceId: string,
  ): { name: string | null; slug: string | null } => {
    const errors: { name: string | null; slug: null | string } = { name: null, slug: null };

    // Get the target craftspace ID
    const targetCraftspaceId = craftspaceId || getDefaultCraftspaceId();

    // Check for duplicate names in the same craftspace
    const existingProjectWithSameName = projects.find(
      (project) =>
        project.craftspace_id === targetCraftspaceId &&
        project.name.toLowerCase() === name.toLowerCase(),
    );

    if (existingProjectWithSameName) {
      errors.name = "A project with this name already exists in this craftspace";
    }

    // Check for duplicate slugs in the same craftspace
    const existingProjectWithSameSlug = projects.find(
      (project) =>
        project.craftspace_id === targetCraftspaceId &&
        project.data &&
        typeof project.data === "object" &&
        (project.data as Record<string, unknown>).customSlug === slug,
    );

    if (existingProjectWithSameSlug) {
      errors.slug = "A project with this slug already exists in this craftspace";
    }

    return errors;
  };

  // Check if form is valid
  const isFormValid = (): boolean => {
    const name = projectName.trim();
    const slug = projectSlug.trim();

    if (!name) return false;
    if (!slug) return false;

    const craftspaceId = selectedCraftspaceId || getDefaultCraftspaceId();
    const duplicates = checkForDuplicates(name, slug, craftspaceId);

    return !duplicates.name && !duplicates.slug;
  };

  // Show loading state while authentication is being checked
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!user && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-800 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Finalizing sign in...</p>
        </div>
      </div>
    );
  }

  // Redirect to auth when there's no active authenticated session.
  // In Electron, the app works without login (local-only mode).
  if (!user && !isElectron()) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-800 dashboard-container flex flex-col">
      {/* Header */}
      <header className={`bg-gray-800 border-b border-gray-700 flex-shrink-0 ${isElectron() ? "electron-drag" : ""}`}>
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className={`flex items-center space-x-4 ${isElectron() ? "pl-20 electron-no-drag" : ""}`}>
              <h1 className="text-2xl font-bold text-white">360Crafter</h1>
            </div>
            <div className={`flex items-center space-x-4 ${isElectron() ? "electron-no-drag" : ""}`}>
              {thumbnailProcessing && (
                <div className="flex items-center space-x-2 text-gray-400 bg-gray-700 px-3 py-1 rounded-lg">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-400"></div>
                  <span className="text-sm">Generating thumbnails...</span>
                </div>
              )}

              {!user && isElectron() && (
                <Button
                  onClick={() => navigate("/login")}
                  variant="outline"
                  className="border-gray-600 text-white hover:bg-gray-700"
                >
                  <User size={16} className="mr-2" />
                  Sign In to Crafter Cloud
                </Button>
              )}
            </div>
          </div>
      </header>

      {/* Sidebar + Content Row */}
      <div className="flex flex-1">
        {/* Craftspace Sidebar */}
        <CraftspaceSidebar
          craftspaces={craftspaces}
          selectedCraftspaceId={activeCraftspaceId}
          onCraftspaceSelect={handleCraftspaceSelect}
          onCreateCraftspace={handleCreateCraftspace}
          activeView={activeView}
          onViewChange={setActiveViewAndPersist}
          user={user}
          userName={userProfile?.full_name}
          onCraftspaceSettings={(id) => navigate(`/craftspace/${id}/settings`)}
          onUserPreferences={() => navigate("/user-preferences")}
          onSignOut={user ? handleSignOut : undefined}
          className="flex-shrink-0"
        />

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8 flex-1 bg-gray-800">
          {activeView === "published" && (
            <div>
              <h2 className="text-2xl font-bold text-white mb-8">Published</h2>
              <PublishedToursList />
            </div>
          )}
          {activeView.startsWith("variables-") && (
            <CraftspaceVariables
              craftspaceId={activeCraftspaceId || undefined}
              embedded
              section={
                activeView === "variables-colors"
                  ? "colors"
                  : activeView === "variables-sizes"
                    ? "sizes"
                    : activeView === "variables-icons"
                      ? "icons"
                      : "fonts"
              }
            />
          )}
          {activeView === "projects" && <div>
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center space-x-4">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{getActiveCraftspaceName()}</h2>
                <p className="text-gray-400 flex items-center">
                  {(() => {
                    const activeCraftspace = craftspaceGroups.find(
                      (g) => g.craftspaceId === activeCraftspaceId,
                    );
                    if (activeCraftspace?._source === "local") {
                      return (
                        <>
                          <HardDrive size={16} className="mr-2 text-white" />
                          Local Storage
                        </>
                      );
                    } else if (activeCraftspace?.storageProvider === "custom-s3") {
                      const text =
                        s3ProviderName && s3BucketName
                          ? `Hosted on ${s3ProviderName} in ${s3BucketName} bucket`
                          : s3ProviderName
                            ? `Hosted on ${s3ProviderName}`
                            : "Hosted on your custom S3 storage";
                      return (
                        <>
                          <Server size={16} className="mr-2 text-white" />
                          {text}
                        </>
                      );
                    } else {
                      return (
                        <>
                          <Cloud size={16} className="mr-2 text-white" />
                          Crafter Cloud
                        </>
                      );
                    }
                  })()}
                </p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white text-xs font-semibold tracking-wide px-3 py-1.5 h-auto border-0"
              >
                <Plus size={13} className="mr-1.5" />
                NEW PROJECT
              </Button>
              {/* Manual generation still available via menu button if needed; hide primary CTA to avoid noise */}

              <Dialog
                open={createCraftspaceDialogOpen}
                onOpenChange={handleCreateCraftspaceDialogChange}
              >
                <DialogContent className="bg-gray-800 border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Craftspace</DialogTitle>
                    <DialogDescription className="text-gray-300">
                      Create a new craftspace to organize and store your projects.
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={createCraftspace} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="craftspace-name" className="text-gray-300">
                        Craftspace Name
                      </Label>
                      <div className="relative">
                        <Input
                          id="craftspace-name"
                          name="name"
                          value={craftspaceNameInput}
                          onChange={(e) => handleCraftspaceNameChange(e.target.value)}
                          required
                          className="bg-gray-700 border-gray-600 text-white"
                          placeholder="Enter craftspace name or domain (e.g., 360creators.com)"
                        />
                        {isFetchingFavicon && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span className="text-gray-500">Craftspace slug:</span>
                        <span className={`font-mono ${craftspaceSlugTaken ? "text-red-400" : "text-gray-300"}`}>
                          {getCraftspaceSlugPreview(craftspaceNameInput)}
                        </span>
                        {craftspaceSlugTaken && (
                          <span className="text-red-400">— already taken</span>
                        )}
                      </div>

                      {/* Favicon Preview */}
                      {isUrl(craftspaceNameInput) && (
                        <div className="flex items-center space-x-2 p-2 bg-gray-800 rounded border border-gray-600">
                          <div className="w-6 h-6 flex items-center justify-center">
                            {faviconPreview ? (
                              <img
                                src={faviconPreview}
                                alt="Favicon preview"
                                className="w-full h-full rounded-sm object-cover"
                                onError={(e) => {
                                  const target = e.target as HTMLImageElement;
                                  target.style.display = "none";
                                }}
                              />
                            ) : isFetchingFavicon ? (
                              <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <div className="w-full h-full bg-gray-600 rounded-sm flex items-center justify-center">
                                <span className="text-xs text-gray-400">?</span>
                              </div>
                            )}
                          </div>
                          <div className="text-sm text-gray-300">
                            {faviconPreview ? (
                              <span className="text-green-400">
                                ✓ Favicon found - will be used as craftspace icon
                              </span>
                            ) : isFetchingFavicon ? (
                              <span className="text-blue-400">Fetching favicon...</span>
                            ) : (
                              <span className="text-yellow-400">
                                URL detected - searching for favicon...
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Storage Provider Selection — hidden in local Electron mode */}
                    <div className="space-y-3">
                      <Label className="text-gray-300">Storage Provider</Label>
                      <div className={`grid ${isElectron() ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
                        {isElectron() && (
                          <button
                            type="button"
                            className={`p-4 border rounded-lg transition-colors text-left ${
                              selectedStorageProvider === "local"
                                ? "border-blue-500 bg-blue-500/10 cursor-pointer"
                                : "border-gray-600 bg-gray-800 hover:bg-gray-700 cursor-pointer"
                            }`}
                            onClick={() => setSelectedStorageProvider("local")}
                            aria-label="Local storage provider"
                          >
                            <div className="flex items-center space-x-3">
                              <HardDrive className="w-5 h-5 text-blue-400" />
                              <div>
                                <div className="font-medium text-white">Local</div>
                                <div className="text-sm text-gray-400">
                                  Stored on this Mac
                                </div>
                              </div>
                            </div>
                          </button>
                        )}
                        <button
                          type="button"
                          className={`p-4 border rounded-lg transition-colors text-left ${
                            !!user?.id && subscriptionInfo?.canCreate360CrafterCraftspace
                              ? selectedStorageProvider === "360crafter"
                                ? "border-blue-500 bg-blue-500/10 cursor-pointer"
                                : "border-gray-600 bg-gray-800 hover:bg-gray-700 cursor-pointer"
                              : "border-gray-700 bg-gray-800/50 cursor-not-allowed opacity-60"
                          }`}
                          onClick={() => {
                            if (user?.id && subscriptionInfo?.canCreate360CrafterCraftspace) {
                              setSelectedStorageProvider("360crafter");
                            }
                          }}
                          disabled={!user?.id || !subscriptionInfo?.canCreate360CrafterCraftspace}
                          aria-label="Crafter Cloud storage provider"
                        >
                          <div className="flex items-center space-x-3">
                            <Cloud className="w-5 h-5 text-blue-400" />
                            <div>
                              <div className="font-medium text-white">Crafter Cloud</div>
                              <div className="text-sm text-gray-400">
                                {subscriptionInfo?.canCreate360CrafterCraftspace
                                  ? "Managed cloud storage"
                                  : user?.id
                                    ? "Subscription required"
                                    : "Sign in required"}
                              </div>
                            </div>
                          </div>
                          {user?.id && !subscriptionInfo?.canCreate360CrafterCraftspace && (
                            <div className="mt-2 text-xs text-yellow-400">
                              {getUpgradeMessage("360crafter_cloud").action}
                            </div>
                          )}
                        </button>

                        <button
                          type="button"
                          className={`p-4 border rounded-lg transition-colors text-left ${
                            !!user?.id && subscriptionInfo?.canCreateBYOS3Craftspace
                              ? selectedStorageProvider === "custom-s3"
                                ? "border-blue-500 bg-blue-500/10 cursor-pointer"
                                : "border-gray-600 bg-gray-800 hover:bg-gray-700 cursor-pointer"
                              : "border-gray-700 bg-gray-800/50 cursor-not-allowed opacity-60"
                          }`}
                          onClick={() => {
                            if (user?.id && subscriptionInfo?.canCreateBYOS3Craftspace) {
                              setSelectedStorageProvider("custom-s3");
                            }
                          }}
                          disabled={!user?.id || !subscriptionInfo?.canCreateBYOS3Craftspace}
                          aria-label="Custom S3 storage provider"
                        >
                          <div className="flex items-center space-x-3">
                            <Server className="w-5 h-5 text-blue-400" />
                            <div>
                              <div className="font-medium text-white">BYO - S3</div>
                              <div className="text-sm text-gray-400">
                                {subscriptionInfo?.canCreateBYOS3Craftspace
                                  ? "Bring Your Own S3 provider"
                                  : user?.id
                                    ? "Subscription or payment required"
                                    : "Sign in required"}
                              </div>
                            </div>
                          </div>
                          {user?.id && !subscriptionInfo?.canCreateBYOS3Craftspace && (
                            <div className="mt-2 text-xs text-yellow-400">
                              {getUpgradeMessage("byo_s3").action}
                            </div>
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCreateCraftspaceDialogOpen(false)}
                        className="border-gray-600 text-gray-300 hover:bg-gray-800"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={
                          creatingCraftspace ||
                          (selectedStorageProvider === "360crafter" &&
                            (!user?.id || !subscriptionInfo?.canCreate360CrafterCraftspace)) ||
                          (selectedStorageProvider === "custom-s3" &&
                            (!user?.id || !subscriptionInfo?.canCreateBYOS3Craftspace))
                        }
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                      >
                        {creatingCraftspace
                          ? "Creating..."
                          : (selectedStorageProvider === "360crafter" ||
                              selectedStorageProvider === "custom-s3") &&
                              !user?.id
                            ? "Sign In Required"
                            : selectedStorageProvider === "360crafter" &&
                                !subscriptionInfo?.canCreate360CrafterCraftspace
                              ? "No Storage Access"
                              : selectedStorageProvider === "custom-s3" &&
                                  !subscriptionInfo?.canCreateBYOS3Craftspace
                                ? "No Storage Access"
                            : "Create Craftspace"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog
                open={createDialogOpen}
                onOpenChange={(open) => {
                  setCreateDialogOpen(open);
                  if (open) {
                    if (!selectedCraftspaceId) {
                      setSelectedCraftspaceId("");
                      setShowCraftspaceDropdown(false);
                    }
                    // Reset form when opening
                    resetProjectForm();
                  }
                }}
              >
                <DialogContent className="bg-gray-800 border-gray-700">
                  <DialogHeader>
                    <DialogTitle className="text-white">Create New Project</DialogTitle>
                    <DialogDescription className="text-gray-300">
                      {selectedCraftspaceId
                        ? `Give your virtual tour project a name and description. This project will be created in the "${getCraftspaceName(selectedCraftspaceId)}" craftspace.`
                        : "Give your virtual tour project a name and description."}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={createProject} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="text-gray-300">
                        Project Name
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        required
                        className={`bg-gray-700 border-gray-600 text-white ${
                          validationErrors.name ? "border-red-500" : ""
                        }`}
                        placeholder="Enter project name"
                        value={projectName}
                        onChange={handleProjectNameChange}
                        onBlur={handleProjectNameBlur}
                      />
                      {validationErrors.name && (
                        <p className="text-xs text-red-400">{validationErrors.name}</p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="slug" className="text-gray-300">
                        Project Slug
                      </Label>
                      <div className="flex items-center space-x-2">
                        <Input
                          id="slug"
                          name="slug"
                          className={`bg-gray-700 border-gray-600 text-white flex-1 ${
                            validationErrors.slug ? "border-red-500" : ""
                          }`}
                          placeholder="Auto-generated slug"
                          value={projectSlug}
                          onChange={handleSlugChange}
                        />
                        {isSlugCustomized && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              const newSlug = generateSlug(projectName);
                              setProjectSlug(newSlug);
                              setIsSlugCustomized(false);

                              // Validate after reset
                              const craftspaceId = selectedCraftspaceId || getDefaultCraftspaceId();
                              const duplicates = checkForDuplicates(
                                projectName,
                                newSlug,
                                craftspaceId,
                              );
                              setValidationErrors(duplicates);
                            }}
                            className="border-gray-600 text-gray-300 hover:bg-gray-800 whitespace-nowrap"
                          >
                            Reset
                          </Button>
                        )}
                      </div>
                      {validationErrors.slug && (
                        <p className="text-xs text-red-400">{validationErrors.slug}</p>
                      )}
                      <p className="text-xs text-gray-400">
                        This slug will be used in the published tour URL. You can customize it or
                        let it auto-generate from the project name.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description" className="text-gray-300">
                        Description (Optional)
                      </Label>
                      <Input
                        id="description"
                        name="description"
                        className="bg-gray-700 border-gray-600 text-white"
                        placeholder="Enter project description"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-300">Craftspace</Label>
                      <div className="relative craftspace-dropdown">
                        <button
                          type="button"
                          onClick={() => setShowCraftspaceDropdown(!showCraftspaceDropdown)}
                          className="w-full flex items-center justify-between bg-gray-800 border border-gray-600 text-white px-3 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                          <span>
                            {selectedCraftspaceId
                              ? getCraftspaceName(selectedCraftspaceId)
                              : getDefaultCraftspaceId()
                                ? getCraftspaceName(getDefaultCraftspaceId())
                                : "Select Craftspace"}
                          </span>
                          <ChevronDown size={16} className="text-gray-400" />
                        </button>

                        {showCraftspaceDropdown && (
                          <div className="absolute z-10 w-full mt-1 bg-gray-800 border border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto">
                            {craftspaces.length === 0 ? (
                              <div className="px-3 py-2 text-gray-400 text-sm">
                                No craftspaces available
                              </div>
                            ) : (
                              craftspaces.map((craftspace) => (
                                <button
                                  key={craftspace.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedCraftspaceId(craftspace.id);
                                    setShowCraftspaceDropdown(false);
                                    setRecentCraftspace(craftspace.id);

                                    // Re-validate after craftspace change
                                    if (projectName.trim() && projectSlug.trim()) {
                                      const duplicates = checkForDuplicates(
                                        projectName,
                                        projectSlug,
                                        craftspace.id,
                                      );
                                      setValidationErrors(duplicates);
                                    }
                                  }}
                                  className="w-full text-left px-3 py-2 text-white hover:bg-gray-700 focus:bg-gray-700 focus:outline-none"
                                >
                                  {craftspace.name}
                                </button>
                              ))
                            )}
                            <div className="border-t border-gray-600">
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedCraftspaceId("");
                                  setShowCraftspaceDropdown(false);

                                  // Re-validate after craftspace change
                                  if (projectName.trim() && projectSlug.trim()) {
                                    const duplicates = checkForDuplicates(
                                      projectName,
                                      projectSlug,
                                      getDefaultCraftspaceId(),
                                    );
                                    setValidationErrors(duplicates);
                                  }
                                }}
                                className="w-full text-left px-3 py-2 text-white hover:bg-gray-700 focus:bg-gray-700 focus:outline-none"
                              >
                                {getDefaultCraftspaceId()
                                  ? getCraftspaceName(getDefaultCraftspaceId())
                                  : "Default"}{" "}
                                (Default)
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setCreateDialogOpen(false)}
                        className="border-gray-600 text-gray-300 hover:bg-gray-800"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={creating || !isFormValid()}
                        className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
                      >
                        {creating ? "Creating..." : "Create Project"}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Projects List */}
          {loading && !hasLoadedOnce ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 bg-blue-400 rounded-full animate-pulse"></div>
            </div>
          ) : craftspaceGroups.length === 0 ? (
            <div className="text-center py-12">
              <Lock size={64} className="mx-auto text-gray-600 mb-4" />
              <h3 className="text-xl font-semibold text-white mb-2">No projects yet</h3>
              <p className="text-gray-400 mb-6">
                Create your first 360° virtual tour project to get started.
              </p>
              <Button
                onClick={() => setCreateDialogOpen(true)}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus size={16} className="mr-2" />
                Create Your First Project
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {craftspaceGroups
                .filter((group) => {
                  // Always show only one craftspace - the active one, or the first one as fallback
                  let targetCraftspaceId = activeCraftspaceId;

                  // If activeCraftspaceId doesn't exist in the groups, use the first available one
                  if (
                    !targetCraftspaceId ||
                    !craftspaceGroups.some((g) => g.craftspaceId === targetCraftspaceId)
                  ) {
                    targetCraftspaceId = craftspaceGroups[0]?.craftspaceId;
                    // Update the active craftspace to the first available one
                    if (targetCraftspaceId && targetCraftspaceId !== activeCraftspaceId) {
                      setActiveCraftspaceId(targetCraftspaceId);
                    }
                  }

                  return group.craftspaceId === targetCraftspaceId;
                })
                .map((group) => (
                  <div key={group.craftspaceId} className="space-y-3">
                    {/* Projects Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {group.projects.map((project) => {
                        const imageUrl = getProjectImageUrl(project);

                        // Note: Individual project URL refresh is now handled by the global auto-refresh

                        return (
                          <Card
                            key={project.id}
                            className={`group bg-gray-800 border border-gray-500/50 hover:bg-gray-750 transition-all duration-500 cursor-pointer ${
                              visibleProjects.has(project.id)
                                ? "opacity-100 translate-y-0"
                                : "opacity-0 translate-y-4"
                            }`}
                            onClick={() => openProject(project.id)}
                          >
                            {/* Thumbnail — full width, no padding */}
                            <div className="relative aspect-video bg-gray-700 rounded-t-lg overflow-hidden border-b border-gray-600">
                              {imageUrl ? (
                                <CachedProjectThumbnail
                                  panoramaId={project.firstPanorama?.id || ""}
                                  projectName={project.name}
                                  projectId={project.id}
                                  craftspaceId={project.craftspace_id || getDefaultCraftspaceId()}
                                  getThumbnail={getThumbnail}
                                  cacheThumbnailFromUrl={cacheThumbnailFromUrl}
                                  networkThumbnailUrl={imageUrl}
                                />
                              ) : (
                                <div className="flex items-center justify-center h-full text-gray-500">
                                  <Lock size={28} />
                                </div>
                              )}
                            </div>

                            {/* Footer */}
                            <div className="flex items-center justify-between px-3 py-2.5 transition-colors group-hover:bg-white/5 rounded-b-lg">
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-white truncate">
                                  {project.name}
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {timeAgo(project.updated_at || project.created_at)}
                                </p>
                              </div>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-gray-500 hover:text-white hover:bg-gray-700 flex-shrink-0 ml-2 h-7 w-7 p-0"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <Settings size={14} />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  className="bg-gray-800 border-gray-700"
                                  align="end"
                                >
                                  <DropdownMenuItem
                                    className="text-white hover:bg-gray-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openProject(project.id);
                                    }}
                                  >
                                    <PanoramaIcon size={16} className="mr-2" />
                                    Hotspot Editor
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-white hover:bg-gray-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/${project.id}/skin`);
                                    }}
                                  >
                                    <Layers size={16} className="mr-2" />
                                    Skin
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-white hover:bg-gray-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/${project.id}/settings`);
                                    }}
                                  >
                                    <Settings size={16} className="mr-2" />
                                    Project Settings
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-white hover:bg-gray-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRenameOpen(project);
                                    }}
                                  >
                                    <Pencil size={16} className="mr-2" />
                                    Rename
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-white hover:bg-gray-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      duplicateProject(project);
                                    }}
                                  >
                                    <Copy size={16} className="mr-2" />
                                    Duplicate
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-white hover:bg-gray-800"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleMoveProject(project);
                                    }}
                                  >
                                    <FolderOpen size={16} className="mr-2" />
                                    Move to Craftspace
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    className="text-red-400 hover:bg-red-900/20"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDelete(project);
                                    }}
                                  >
                                    <Trash2 size={16} className="mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                ))}
            </div>
          )}
          </div>}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Delete Project</DialogTitle>
            <DialogDescription className="text-gray-300">
              This action cannot be undone. This will permanently delete your project and all
              associated data.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-confirmation" className="text-gray-300">
                Type the project name "{projectToDelete?.name}" to confirm deletion:
              </Label>
              <Input
                id="delete-confirmation"
                value={deleteConfirmation}
                onChange={(e) => setDeleteConfirmation(e.target.value)}
                onKeyDown={(e) => {
                  if (
                    e.key === "Enter" &&
                    !deleting &&
                    deleteConfirmation === projectToDelete?.name
                  ) {
                    confirmDelete();
                  }
                }}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Enter project name"
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setDeleteDialogOpen(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
                disabled={deleting}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmation !== projectToDelete?.name}
                className="bg-red-600 hover:bg-red-700"
              >
                {deleting ? "Deleting..." : "Delete Project"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Project Dialog */}
      <Dialog
        open={renameDialogOpen}
        onOpenChange={(open) => {
          setRenameDialogOpen(open);
          if (!open) {
            setProjectToRename(null);
            setRenameValue("");
            setRenameError(null);
          }
        }}
      >
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Rename Project</DialogTitle>
            <DialogDescription className="text-gray-300">
              Enter a new name for "{projectToRename?.name}".
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rename-project" className="text-gray-300">
                Project name
              </Label>
              <Input
                id="rename-project"
                value={renameValue}
                onChange={(e) => {
                  const value = e.target.value;
                  setRenameValue(value);
                  setRenameError(validateRenameName(value, projectToRename));
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !renaming) {
                    confirmRename();
                  }
                }}
                className="bg-gray-700 border-gray-600 text-white"
                placeholder="Enter project name"
                disabled={renaming}
              />
              {renameError && <p className="text-xs text-red-400">{renameError}</p>}
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setRenameDialogOpen(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
                disabled={renaming}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmRename}
                disabled={renaming || !!renameError}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed"
              >
                {renaming ? "Renaming..." : "Rename Project"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Craftspace Dialog */}
      <Dialog open={moveCraftspaceDialogOpen} onOpenChange={setMoveCraftspaceDialogOpen}>
        <DialogContent className="bg-gray-800 border-gray-700">
          <DialogHeader>
            <DialogTitle className="text-white">Move Project to Craftspace</DialogTitle>
            <DialogDescription className="text-gray-300">
              Move "{projectToMove?.name}" to a different craftspace.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="target-craftspace" className="text-gray-300">
                Select Target Craftspace:
              </Label>
              <CraftspaceSelector
                craftspaces={craftspaces}
                selectedCraftspaceId={selectedTargetCraftspace}
                onCraftspaceSelect={setSelectedTargetCraftspace}
                placeholder="Select a craftspace..."
                disabled={moving}
                excludeCraftspaceId={projectToMove?.craftspace_id}
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setMoveCraftspaceDialogOpen(false)}
                className="border-gray-600 text-gray-300 hover:bg-gray-800"
                disabled={moving}
              >
                Cancel
              </Button>
              <Button
                onClick={moveProject}
                disabled={moving || !selectedTargetCraftspace}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {moving ? "Moving..." : "Move Project"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* S3 Connection Dialog */}
      <S3ConnectionDialog
        open={s3ConnectionDialogOpen}
        onClose={() => {
          setS3ConnectionDialogOpen(false);
        }}
        craftspaceId=""
        craftspaceName={pendingCraftspaceName}
        deferCredentialSave
        onConnectionComplete={handleS3ConnectionComplete}
      />
    </div>
  );
};

export default Dashboard;
